import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { Controller, useFormContext } from 'react-hook-form';

import type {
  PhoneConsentFieldsProps,
  PhoneConsentFormValues
} from './PhoneConsentFields.types';

const PhoneConsentFields = <TFormValues extends PhoneConsentFormValues>({
  control,
  errors,
  phoneValidationMode = 'optional',
  consentEditable = true,
  showConsentControls = true
}: PhoneConsentFieldsProps<TFormValues>) => {
  const { trigger } = useFormContext();
  const consentControl =
    control as unknown as PhoneConsentFieldsProps['control'];
  const consentErrors = errors as unknown as PhoneConsentFieldsProps['errors'];
  const consentError =
    (consentErrors.sms_consent?.message as string | undefined) ||
    (consentErrors.tos_consent?.message as string | undefined);

  return (
    <Stack spacing={1}>
      <Typography variant="overline" color="text.secondary">
        Mobile
      </Typography>
      <Stack direction="row" sx={{ alignItems: 'flex-start' }} spacing={1}>
        <Box sx={{ flex: 3 }}>
          <InputLabel>Phone Number</InputLabel>
          <Controller
            name="phone_number"
            control={consentControl}
            rules={
              phoneValidationMode === 'required'
                ? { required: true }
                : {
                    validate: (v) =>
                      !v ||
                      /^\+?[\d\s()-]{7,17}$/.test(v) ||
                      'Enter a valid phone number (e.g. +1 (555) 000-0000)'
                  }
            }
            render={({ field }) => (
              <MuiTelInput
                {...field}
                value={field.value ?? ''}
                fullWidth
                defaultCountry="US"
                autoComplete="off"
                error={!!consentErrors.phone_number}
                helperText={consentErrors.phone_number?.message as string}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <InputLabel>Post-connect digits</InputLabel>
          <Controller
            name="phone_extension"
            control={consentControl}
            rules={{
              validate: (v) =>
                !v ||
                /^[0-9A-Da-dwW,#*]{1,20}$/.test(v) ||
                'May only contain: 0-9, A-D, #, *, W (1s pause), w (0.5s pause), , (comma)'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                placeholder="e.g. 1, W2"
                fullWidth
                autoComplete="off"
                error={!!consentErrors.phone_extension}
                helperText={consentErrors.phone_extension?.message as string}
              />
            )}
          />
        </Box>
      </Stack>
      <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>
        Office
      </Typography>
      <Stack direction="row" sx={{ alignItems: 'flex-start' }} spacing={1}>
        <Box sx={{ flex: 3 }}>
          <InputLabel>Phone Number (optional)</InputLabel>
          <Controller
            name="office_phone_number"
            control={consentControl}
            rules={{
              validate: (v) =>
                !v ||
                /^\+?[\d\s()-]{7,17}$/.test(v) ||
                'Enter a valid phone number (e.g. +1 (555) 000-0000)'
            }}
            render={({ field }) => (
              <MuiTelInput
                {...field}
                value={field.value ?? ''}
                fullWidth
                defaultCountry="US"
                autoComplete="off"
                error={!!consentErrors.office_phone_number}
                helperText={
                  consentErrors.office_phone_number?.message as string
                }
              />
            )}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <InputLabel>Post-connect digits</InputLabel>
          <Controller
            name="office_phone_extension"
            control={consentControl}
            rules={{
              validate: (v) =>
                !v ||
                /^[0-9A-Da-dwW,#*]{1,20}$/.test(v) ||
                'May only contain: 0-9, A-D, #, *, W (1s pause), w (0.5s pause), , (comma)'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                placeholder="e.g. 1, W2"
                fullWidth
                autoComplete="off"
                error={!!consentErrors.office_phone_extension}
                helperText={
                  consentErrors.office_phone_extension?.message as string
                }
              />
            )}
          />
        </Box>
      </Stack>
      {showConsentControls && (
        <>
          <Controller
            name="sms_consent"
            control={consentControl}
            rules={
              consentEditable
                ? {
                    validate: (smsConsent, formValues) =>
                      !smsConsent ||
                      formValues.tos_consent ||
                      'To consent to SMS, you must also accept Terms of Service.'
                  }
                : undefined
            }
            render={({ field }) => (
              <FormControlLabel
                sx={{ alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    {...field}
                    checked={field.value || false}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      trigger(['sms_consent', 'tos_consent']);
                    }}
                    sx={{ py: 0.5, pl: 0 }}
                    disabled={!consentEditable}
                  />
                }
                label={
                  <Typography sx={{ pt: 0.75 }}>
                    I consent to receive SMS text messages from Pondurance
                    regarding security events that may require my attention.
                    Message and data rates may apply. Message frequency varies.
                  </Typography>
                }
              />
            )}
          />
          <Controller
            name="tos_consent"
            control={consentControl}
            rules={
              consentEditable
                ? {
                    validate: (tosConsent, formValues) =>
                      !formValues.sms_consent ||
                      tosConsent ||
                      'To consent to SMS, you must also accept Terms of Service.'
                  }
                : undefined
            }
            render={({ field }) => (
              <FormControlLabel
                sx={{ alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    {...field}
                    checked={field.value || false}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      trigger(['sms_consent', 'tos_consent']);
                    }}
                    sx={{ py: 0.5, pl: 0 }}
                    disabled={!consentEditable}
                  />
                }
                label={
                  <Typography sx={{ pt: 0.75 }}>
                    I accept the{' '}
                    <a
                      href="https://www.pondurance.com/text-alert-policy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.pondurance.com/privacy-addendum"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </Typography>
                }
              />
            )}
          />
          {consentError && (
            <FormHelperText error>{consentError}</FormHelperText>
          )}
          {!consentEditable && (
            <Alert severity="info">
              Consent can only be edited by the user themselves.
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
};

export default PhoneConsentFields;
